import { Breadcrumb, Button, Card, Checkbox, Col, DatePicker, Form, Flex, Radio, Row, Skeleton, Typography } from "antd";
import FormItem from "antd/es/form/FormItem";
import { useTranslation } from "react-i18next";
import FormAntd from "components/form/FormProvide/FormAntd";
import { FormDatePicker, FormInput, FormRadio, FormSelect } from "components/form/FormProvide";
import ContributionRateTable from "../table/ContributionRateTable";
import VestingRuleTable from "../table/VestingRuleTable";
import useFund from "../hook/useFund";
import styles from "../styles/Fund.module.css";
import {
    CRITERIA_BASIS,
    CONTRIBUTION_CALCULATION_BASIS,
    ENTRY_EXIT_POLICY,
    FUND_STATUS,
    FUND_TYPE,
    MEMBERSHIP_ELIGIBILITY,
    TIME_UNIT,
    findPolicy,
    toOptions,
} from "../schemas/FundSchemas";
import { MainLayout } from "components/layouts";
import { Space } from "antd/lib";
import { DatePickerForm } from "components/form";
import { useLocation, useNavigate } from "react-router-dom";
import DisabledContext from "antd/es/config-provider/DisabledContext";
import { PAYROLL_PAGE } from "features/payrollSetting/schemas/payrollSchema";

const { Title } = Typography;

const FundFormPage = () => {
    const { t, i18n } = useTranslation();
    const { form, isLoading, isSaving, pageTitle, positionOptions, onFinish, onCancel } = useFund();

    // ทั้งหน้าฟอร์มขึ้นกับ "ประเภทกองทุน" — PROVIDENT_FUND ใช้ฟอร์มเต็ม, ประเภทอื่นใช้ฟอร์มแบบง่าย
    // ทั้งสองกรณีใช้ contributionRateConditions โครงสร้างเดียวกัน ต่างกันแค่ฟอร์มแบบง่ายผูกกับ rules[0] โดยตรง
    const fundType = Form.useWatch("fundType", form);
    const isProvidentFund = fundType === "PROVIDENT_FUND";
    const location = useLocation();
    const navigate = useNavigate();


    const isViewMode = location.pathname.includes("/configuration/payroll/fund/view/");
    console.log("isViewMode", isViewMode);

    if (isLoading) {
        return (
            <MainLayout>
                <Space direction="vertical" className={styles.container} size={24}>
                    <Flex vertical>
                        <Breadcrumb items={[
                            { title: t("home.header"), href: "/" },
                            { title: t("employee.header"), href: "/employee" },
                            { title: t("employee.all"), href: "/employee" },
                            { title: t("holiday.label.add_employee") },
                        ]}
                        />
                        <h2>{t("holiday.label.add_employee")}</h2>
                    </Flex>
                    <Card>
                        <Skeleton active paragraph={{ rows: 12 }} />
                    </Card>
                </Space>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Space direction="vertical" className={styles.container} size={24}>
            </Space>
            <FormAntd form={form} onFinish={onFinish}>
                <Title level={4} style={{ marginBottom: 24 }}>
                    {pageTitle}
                </Title>

                {/* ---------- ประเภทกองทุน ---------- */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>{t("fund.label.fundType") || "ประเภทกองทุน"}</div>
                    <Row gutter={16}>
                        <Col span={12}>
                            <FormSelect
                                name="fundType"
                                vertical
                                size="large"
                                placeholder={t("fund.placeholder.please_select") || "กรุณาเลือก"}
                                options={toOptions(FUND_TYPE, i18n.language)}
                                sortBy="none"
                                rules={[{ required: true }]}
                                allowClear
                                disabled={isViewMode}
                            />
                        </Col>
                    </Row>
                </div>

                {/* ---------- รายละเอียดกองทุน ---------- */}
                <div className={styles.card}>
                    <div className={styles.sectionTitle}>{t("fund.label.fundDetail") || "รายละเอียดกองทุน"}</div>
                    <Row gutter={16}>
                        <Col span={8}>
                            <FormInput
                                disabled={isViewMode}
                                name="fundCode"
                                vertical
                                size="large"
                                label={t("fund.label.fundCode") || "เลขที่กองทุน"}
                                required
                                placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                            // rules={[{ required: true }]}
                            />
                        </Col>
                        <Col span={8}>
                            <FormInput
                                disabled={isViewMode}
                                name="fundNameTh"
                                vertical
                                size="large"
                                label={t("fund.label.fundNameTh") || "ชื่อกองทุน (ไทย)"}
                                required
                                placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                            // rules={[{ required: true }]}
                            />
                        </Col>
                        <Col span={8}>
                            <FormInput
                                disabled={isViewMode}
                                name="fundNameEn"
                                vertical
                                size="large"
                                label={t("fund.label.fundNameEn") || "ชื่อกองทุน (อังกฤษ)"}
                                required
                                placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                            // rules={[{ required: true}]}
                            />
                        </Col>

                        <Col span={8}>
                            <FormInput
                                disabled={isViewMode}
                                name="securitiesCompany"
                                vertical
                                size="large"
                                label={t("fund.label.securitiesCompany") || "บริษัทหลักทรัพย์"}
                                placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                            />
                        </Col>
                        <Col span={8}>
                            <FormDatePicker
                                label={t("fund.label.fundStartDate") || "วันที่เริ่มกองทุน"}
                                disabled={isViewMode}
                                name="fundStartDate"
                                required
                                // rules={[{ required: true }]}
                                size="large"
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                            >
                            </FormDatePicker>
                        </Col>
                        <Col span={8}>
                            <FormRadio
                                disabled={isViewMode}
                                options={toOptions(FUND_STATUS, i18n.language)} label={t("fund.label.status") || "สถานะ"} name="statusFlag" initialValue="ACTIVE">
                            </FormRadio>
                        </Col>
                    </Row>
                </div>
                {isProvidentFund ? (
                    <>
                        {/* ---------- กฎการคำนวณ / สิทธิ ---------- */}
                        <div className={styles.card}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <FormRadio
                                        disabled={isViewMode}
                                        label={t("fund.label.contributionCalculationBasis") || "คำนวณนำส่งกองทุน"}
                                        name="contributionCalculationBasis"
                                        // rules={[{ required: true, message: "" }]}
                                        options={toOptions(CONTRIBUTION_CALCULATION_BASIS, i18n.language)}
                                    >
                                    </FormRadio>
                                </Col>
                                <Col span={12}>
                                    <FormRadio
                                        disabled={isViewMode}
                                        label={t("fund.label.membershipEligibility") || "สิทธิการเป็นสมาชิก"}
                                        name="membershipEligibility"
                                        rules={[{ required: true, message: "" }]}
                                        options={toOptions(MEMBERSHIP_ELIGIBILITY, i18n.language)}
                                    >
                                    </FormRadio>
                                </Col>
                                <Col span={12}>
                                    <FormRadio
                                        disabled={isViewMode}
                                        label={t("fund.label.contributionRateCriteria") || "เกณฑ์กำหนดอัตราเงินสะสมและเงินสมทบ"}
                                        name="contributionRateCriteria"
                                        rules={[{ required: true, message: "" }]}
                                        options={toOptions(CRITERIA_BASIS, i18n.language)}
                                    >
                                    </FormRadio>
                                </Col>
                                <Col span={12}>
                                    <FormRadio
                                        label={t("fund.label.benefitEligibilityCriteria") || "เกณฑ์การได้รับสิทธิในเงินสมทบ"}
                                        disabled={isViewMode}
                                        name="benefitEligibilityCriteria"
                                        rules={[{ required: true, message: "" }]}
                                        options={toOptions(CRITERIA_BASIS, i18n.language)}
                                    >
                                    </FormRadio>
                                </Col>
                            </Row>
                        </div>

                        {/* ---------- เงื่อนไขอัตราเงินสะสม ---------- */}
                        <div className={styles.card}>

                            <Row>

                                <Col span={12}>
                                    <div className={styles.sectionTitle}>
                                        {t("fund.label.contributionRateConditions") || "เงื่อนไขอัตราเงินสะสม"}
                                    </div>

                                    <FormItem
                                        name={["contributionRateConditions", "companyMatchNotExceedEmployeeRate"]}
                                        valuePropName="checked"
                                        style={{ marginBottom: 4 }}
                                    >
                                        <Checkbox>
                                            {t("fund.label.companyMatchNotExceedEmployeeRate") ||
                                                "บริษัทสมทบในอัตราไม่เกินยอดสะสมของพนักงาน"}
                                        </Checkbox>
                                    </FormItem>

                                    <FormItem
                                        name={["contributionRateConditions", "definedByPosition"]}
                                        valuePropName="checked"
                                        style={{ marginBottom: 4 }}
                                    >
                                        <Checkbox>
                                            {t("fund.label.definedByPosition") || "กำหนดสิทธิ"}
                                        </Checkbox>
                                    </FormItem>


                                    <FormItem
                                        noStyle
                                        shouldUpdate={(prev, curr) =>
                                            prev?.contributionRateConditions?.definedByPosition !==
                                            curr?.contributionRateConditions?.definedByPosition
                                        }
                                    >
                                        {({ getFieldValue }) => {
                                            const definedByPosition = getFieldValue([
                                                "contributionRateConditions",
                                                "definedByPosition",
                                            ]);

                                            if (!definedByPosition) {
                                                return null;
                                            }

                                            return (
                                                <FormItem
                                                    name={[
                                                        "contributionRateConditions",
                                                        "scope",
                                                    ]}
                                                    style={{ marginBottom: 16 }}
                                                >
                                                    <Radio.Group
                                                        disabled={isViewMode}
                                                    >
                                                        <Radio value="ORGANIZATION">
                                                            {t("fund.label.organization") || "ทั้งองค์กร"}
                                                        </Radio>

                                                        <Radio value="POSITION">
                                                            {t("fund.label.position") || "ตามตำแหน่ง"}
                                                        </Radio>
                                                    </Radio.Group>
                                                </FormItem>
                                            );
                                        }}
                                    </FormItem>
                                </Col>
                                <Col span={12}>

                                    <div className={styles.sectionTitle}>
                                        {t("fund.label.vestingRules") || "การได้รับสิทธิในเงินสมทบ"}
                                    </div>

                                    <FormItem
                                        name={["vestingConditions", "definedByPosition"]}
                                        valuePropName="checked"
                                        style={{ marginBottom: 4 }}
                                        disabled={isViewMode}
                                    >
                                        <Checkbox disabled={isViewMode}
                                        >
                                            {t("fund.label.definedByPosition") || "กำหนดสิทธิ"}
                                        </Checkbox>
                                    </FormItem>

                                    <FormItem
                                        noStyle
                                        shouldUpdate={(prev, curr) =>
                                            prev?.vestingConditions?.definedByPosition !==
                                            curr?.vestingConditions?.definedByPosition
                                        }
                                    >
                                        {({ getFieldValue }) => {
                                            const definedByPosition = getFieldValue([
                                                "vestingConditions",
                                                "definedByPosition",
                                            ]);

                                            if (!definedByPosition) {
                                                return null;
                                            }

                                            return (
                                                <FormItem
                                                    name={[
                                                        "vestingConditions",
                                                        "scope",
                                                    ]}
                                                    style={{ marginBottom: 16 }}
                                                >
                                                    <Radio.Group
                                                        disabled={isViewMode}
                                                    >
                                                        <Radio value="ORGANIZATION">
                                                            {t("fund.label.organization") || "ทั้งองค์กร"}
                                                        </Radio>

                                                        <Radio value="POSITION">
                                                            {t("fund.label.position") || "ตามตำแหน่ง"}
                                                        </Radio>
                                                    </Radio.Group>
                                                </FormItem>
                                            );
                                        }}
                                    </FormItem>
                                </Col>
                            </Row>
                            <ContributionRateTable disabled={isViewMode} />
                        </div>

                        {/* ---------- การได้รับสิทธิในเงินสมทบ ---------- */}
                        <div className={styles.card}>
                            <div className={styles.sectionTitle}>{t("fund.label.vestingRules") || "การได้รับสิทธิในเงินสมทบ"}</div>
                            <VestingRuleTable disabled={isViewMode} />
                        </div>

                        {/* ---------- เงื่อนไขการเข้าออกกองทุน ---------- */}
                        <div className={styles.card}>
                            <div className={styles.sectionTitle}>{t("fund.label.entryExitCondition") || "เงื่อนไขการเข้าออกกองทุน"}</div>

                            <FormItem name={["entryExitCondition", "policy"]} rules={[{ required: true, message: "" }]}>
                                <Radio.Group disabled={isViewMode} style={{ width: "100%" }}>
                                    <Flex vertical gap={8}>
                                        {ENTRY_EXIT_POLICY.map((p) => (
                                            <Radio key={p.value} value={p.value}>
                                                {i18n.language === "en" ? p.nameEn : p.name}
                                            </Radio>
                                        ))}
                                    </Flex>
                                </Radio.Group>
                            </FormItem>

                            {/* conditional fields — only shown/required for policies that need them */}
                            <FormItem
                                noStyle
                                shouldUpdate={(prev, curr) => prev?.entryExitCondition?.policy !== curr?.entryExitCondition?.policy}
                            >
                                {({ getFieldValue }) => {
                                    const policy = getFieldValue(["entryExitCondition", "policy"]);
                                    const meta = findPolicy(policy);
                                    if (!meta?.needsWaitingPeriod) return null;

                                    return (
                                        <Row gutter={16} style={{ marginTop: 8 }}>
                                            {meta.needsMaxRejoin && (
                                                <Col span={6}>
                                                    <FormInput
                                                        // type="number"
                                                        disabled={isViewMode}
                                                        name={["entryExitCondition", "maxRejoinCount"]}
                                                        vertical
                                                        size="large"
                                                        label={t("fund.label.maxRejoinCount") || "สมัครใหม่ได้สูงสุด (ครั้ง)"}
                                                        required
                                                        rules={[{ required: true, message: "" }]}
                                                    />
                                                </Col>
                                            )}
                                            <Col span={6}>
                                                <FormInput
                                                    // type="number"
                                                    disabled={isViewMode}
                                                    name={["entryExitCondition", "waitingPeriod"]}
                                                    vertical
                                                    size="large"
                                                    label={t("fund.label.waitingPeriod") || "ระยะเวลาที่ต้องเว้น"}
                                                    required
                                                    rules={[{ required: true, message: "" }]}
                                                />
                                            </Col>
                                            <Col span={6}>
                                                <FormSelect
                                                    name={["entryExitCondition", "waitingPeriodUnit"]}
                                                    vertical
                                                    size="large"
                                                    disabled={isViewMode}
                                                    label={t("fund.label.unit") || "หน่วย"}
                                                    options={toOptions(TIME_UNIT, i18n.language)}
                                                    rules={[{ required: true, message: "" }]}
                                                />
                                            </Col>
                                        </Row>
                                    );
                                }}
                            </FormItem>
                        </div>

                    </>
                ) : fundType && (
                    <div className={styles.card}>
                        <div className={styles.sectionTitle}>{t("fund.label.simpleContributionRate") || "อัตราการสมทบ"}</div>
                        <Row gutter={16}>
                            <Col span={8}>
                                <FormInput
                                    disabled={isViewMode}
                                    name={["contributionRateConditions", "rules", 0, "employeeMaxRatePercent"]}
                                    vertical
                                    size="large"
                                    label={t("fund.label.employeeRate") || "พนักงานสะสมไม่เกิน %"}
                                    required
                                    placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                                />
                            </Col>
                            <Col span={8}>
                                <FormInput
                                    disabled={isViewMode}
                                    name={["contributionRateConditions", "rules", 0, "companyRatePercent"]}
                                    vertical
                                    size="large"
                                    label={t("fund.label.companyRate") || "บริษัทสมทบไม่เกิน %"}
                                    required
                                    placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"}
                                />
                            </Col>
                        </Row>
                    </div>
                )}
                <div className={styles.footerActions}>
                    <DisabledContext.Provider value={false}>
                        <Button
                            size="large"
                            onClick={
                                isViewMode
                                    ? () => navigate("/configuration/payroll", { state: { selectedTab: PAYROLL_PAGE.FUND } })
                                    : onCancel
                            }
                        >
                            {t("common.cancel") || "ยกเลิก"}
                        </Button>
                    </DisabledContext.Provider>
                    {!isViewMode &&
                        <Button type="primary" size="large" htmlType="submit" loading={isSaving}>
                            {t("common.save") || "บันทึก"}
                        </Button>
                    }
                </div>
            </FormAntd>
        </MainLayout>
    );
};

export default FundFormPage;
